import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'auth/AuthProvider';
import { invalidateAfterLegalAcceptance } from 'core/api/invalidation';
import { queryKeys } from 'core/api/queryKeys';
import { humanizeApiError } from 'core/errors/mapFieldErrors';
import { useI18n } from 'core/i18n/I18nProvider';
import { needsLegalAcceptance, paths } from 'core/routing/paths';
import { userApi } from 'features/home/home.api';
import { Button } from 'shared/components/Button';
import { ErrorState, Skeleton } from 'shared/components/Feedback';
import { PageMeta } from 'shared/components/PageMeta';
import { formatDate } from 'shared/utilities/format';
import { SafeHtml } from 'shared/utilities/sanitize';

export function LegalAcceptancePage() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, status, refreshUser } = useAuth();
  const [checked, setChecked] = useState({});
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState('');
  const currentQuery = useQuery({
    queryKey: queryKeys.legalCurrent,
    queryFn: () => userApi.currentLegal(),
  });

  if (status === 'bootstrapping') {
    return <Skeleton rows={6} />;
  }

  if (!needsLegalAcceptance(user)) {
    return <Navigate to={paths.home} replace />;
  }

  const unaccepted = user?.legal?.unaccepted || [];
  const terms = currentQuery.data?.terms;
  const privacy = currentQuery.data?.privacy;
  const requiredDocs = [terms, privacy].filter((document) => {
    if (!document) {
      return false;
    }
    if (unaccepted.some((item) => Number(item.id) === Number(document.id) || item.type === document.type)) {
      return true;
    }
    if (document.type === 'terms' && user?.legal?.terms_required) {
      return true;
    }
    if (document.type === 'privacy' && user?.legal?.privacy_required) {
      return true;
    }
    return unaccepted.length === 0;
  });

  const documents = requiredDocs.length > 0 ? requiredDocs : [terms, privacy].filter(Boolean);
  const allChecked = documents.length > 0 && documents.every((document) => checked[document.id]);

  async function acceptRequired() {
    if (accepting || !allChecked) {
      return;
    }
    setAccepting(true);
    setError('');
    try {
      const ids = [];
      unaccepted.forEach((item) => {
        if (item.id) {
          ids.push(item.id);
        }
      });
      documents.forEach((document) => {
        if (document?.id && !ids.includes(document.id)) {
          ids.push(document.id);
        }
      });
      for (const id of ids) {
        await userApi.acceptLegal(id);
      }
      await invalidateAfterLegalAcceptance(queryClient);
      const next = await refreshUser();
      if (!needsLegalAcceptance(next)) {
        navigate(paths.home, { replace: true });
      }
    } catch (err) {
      setError(humanizeApiError(err, t));
    } finally {
      setAccepting(false);
    }
  }

  if (currentQuery.isLoading) {
    return <Skeleton rows={6} />;
  }
  if (currentQuery.isError) {
    return <ErrorState title={humanizeApiError(currentQuery.error, t)} onRetry={() => currentQuery.refetch()} retryLabel={t('actions.retry')} />;
  }

  return (
    <div className="legal-page">
      <PageMeta titleKey="meta.legal" />
      <h1>{t('legal.acceptanceTitle')}</h1>
      <p className="muted">{t('legal.acceptanceBody')}</p>
      <p className="muted">{t('legal.explicitHint')}</p>
      {documents.map((document) => (
        <article key={document.id} className="card legal-card">
          <h2>{document.title || t(`legal.${document.type}`)}</h2>
          <p className="muted">
            {t('legal.version', { version: document.version })}
            {document.effective_at ? ` · ${t('legal.effective', { date: formatDate(document.effective_at, locale) })}` : ''}
          </p>
          <SafeHtml className="legal-content" html={document.content} />
          <label className="check-row">
            <input
              type="checkbox"
              checked={Boolean(checked[document.id])}
              onChange={(event) => setChecked((current) => ({ ...current, [document.id]: event.target.checked }))}
            />
            <span>{document.type === 'privacy' ? t('auth.acceptPrivacy') : t('auth.acceptTerms')}</span>
          </label>
        </article>
      ))}
      {documents.length === 0 ? <p className="muted">{t('legal.missing')}</p> : null}
      {error ? <p className="field-error" role="alert">{error}</p> : null}
      <Button loading={accepting} disabled={accepting || !allChecked} onClick={acceptRequired}>
        {t('legal.acceptAll')}
      </Button>
    </div>
  );
}
